import React, {
  useState, useContext, useMemo,
  useCallback,
} from 'react';
import clsx from 'clsx';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { Badge } from '@printdeal/ui-library';
import { debounce } from 'es-toolkit';
import type { Navigation, NavigationItem } from '../../../types';
import NavigationLink from '../NavigationLink';
import DesktopNavigationDropdown from './DesktopNavigationDropdown';
import { HeaderContext } from '../../context';
import { addEventLabelForBadges, dispatchDataLayerEventOnNavigationView, dispatchNavigationHotjarEvent } from '../helpers';
import { getBadgeKind } from '../../../../product/selector/helpers/AttributeValueHelper';
import { ExploreDesignTemplateButton } from '../../../../design-template/exploreDesignTemplateButton';

type FeaturedNavigationItemProps = {
  item: NavigationItem;
  className?: string;
  experimentClassName: string;
};

const FeaturedNavigationItem = ({
  item,
  className,
  experimentClassName,
}: FeaturedNavigationItemProps) => {
  const { setIsFeaturedNavigationOpen } = useContext(HeaderContext);
  const [isOpen, setIsOpen] = useState(false);

  // Bogus warning, rule is unable to properly work because of the debounce.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOpen = useCallback(debounce(() => {
    setIsOpen(true);
    setIsFeaturedNavigationOpen(true);

    dispatchNavigationHotjarEvent();

    dispatchDataLayerEventOnNavigationView({
      navType: 'navigation_featured',
      eventLabel: 'categoryLevel1',
    });
    addDataLayerEntry('navigation_featured_show');
  }, 200), []);

  const handleClose = useCallback(() => {
    // Cancel potential debounced invocation of the open callback.
    handleOpen.cancel();

    setIsOpen(false);
    setIsFeaturedNavigationOpen(false);
  }, [handleOpen, setIsFeaturedNavigationOpen]);

  return (
    <div
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      className={className}
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <NavigationLink
        to={item.linkedEntry?.path}
        onFocus={handleOpen}
        navigationType="featured"
        eventLabel={item.title}
        className={clsx(
          'pd-hidden pd-h-14 pd-border-b-2 pd-px-4 lg:pd-inline-flex',
          isOpen && 'pd-border-primary-500 pd-text-primary-500',
        )}
      >
        {item.title}
      </NavigationLink>
      {item.collapsibleContent?.length && (
        <DesktopNavigationDropdown
          unmount
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          data-testid="desktop-featured-navigation-dropdown"
        >
          <ul className="pd-columns-4 pd-gap-6 pd-py-1">
            {item.collapsibleContent.map((collapsibleContent) => {
              if (!collapsibleContent.title) return null;

              return (
                <li key={collapsibleContent.id} className="pd-mb-6 pd-flex pd-break-inside-avoid pd-flex-col pd-gap-2">
                  <NavigationLink
                    to={collapsibleContent.linkedEntry?.path}
                    navigationType="featured"
                    eventLabel={collapsibleContent.title}
                  >
                    {collapsibleContent.title}
                  </NavigationLink>
                  <ul className="pd-flex pd-flex-col pd-gap-1">
                    {collapsibleContent.subItems?.map(({
                      linkedEntry, path, title, id, label,
                    }) => {
                      if (!linkedEntry && !path) {
                        return null;
                      }
                      const navSubItemTitle = linkedEntry ? linkedEntry.title : title;
                      const eventLabelPerBadge = (label && navSubItemTitle)
                        ? addEventLabelForBadges(
                          label.type,
                          navSubItemTitle,
                          label.displayName,
                        ) : navSubItemTitle;
                      return (
                        <li key={id} data-level="3">
                          <NavigationLink
                            to={linkedEntry ? linkedEntry.path : path}
                            navigationType="main"
                            navigationLevel="level3"
                            eventLabel={eventLabelPerBadge}
                            className={clsx(
                              'pd-mr-2 pd-text-sm',
                              experimentClassName,
                            )}
                          >
                            {navSubItemTitle}
                          </NavigationLink>
                          {(label) && (
                            <Badge variant={getBadgeKind(label.type)}>
                              {label.displayName}
                            </Badge>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
          <ExploreDesignTemplateButton isButton label="nav_link" className="pd-flex pd-justify-center" />
        </DesktopNavigationDropdown>
      )}
    </div>
  );
};

type FeaturedNavigationProps = {
  navigation: Navigation;
  className?: string;
  experimentClassName?: string;
};

const FeaturedNavigation = ({
  navigation,
  className,
  experimentClassName = '',
}: FeaturedNavigationProps) => {
  const { isSticky } = useContext(HeaderContext);

  // A maximum of 4 items are allowed.
  const slicedItems = useMemo(
    () => navigation.items?.slice(0, 4) || [],
    [navigation.items],
  );

  if (!slicedItems.length) {
    return null;
  }

  return (
    <section className={clsx(isSticky && 'pd-hidden', className)} data-testid="desktop-featured-navigation">
      <ul className="pd-flex pd-items-center">
        {slicedItems.map((item) => (
          <li key={item.id}>
            <FeaturedNavigationItem item={item} experimentClassName={experimentClassName} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default FeaturedNavigation;
